import { ChangeEvent, useMemo, useCallback } from 'react';
import _isEmpty from 'lodash/isEmpty';
import _some from 'lodash/some';
import _find from 'lodash/find';
import { SelectOption } from '@minecraft.typings';
import { FilterValues } from '../types';
import { getNestedEntity, getUpdatedNavigationSections, searchFilterNameOrCategory } from '../utils';
import { useDrawerContext } from '../context';

type UseDrawerSearchFilterNameOrCategoryResult = {
  isSearching: boolean;
  isCategoryMatchedSearch: boolean;
  noSearchResult: boolean;
  searchValue: string;
  shouldCategoryFilterBeVisible: (filterValue: string) => boolean;
  assignOptionsToCategoryFilters: (filtersOptions: FilterValues) => void;
  getMatchedSearchOptions: (filterValue: string) => SelectOption[];
  handleSearchValueChange: (event: ChangeEvent<HTMLInputElement>) => void;
};

const useDrawerSearchFilterNameOrCategory = (categoryValue?: string): UseDrawerSearchFilterNameOrCategoryResult => {
  const { isSearching, searchValue, navigationSections, setSearchValue, setNavigationSections } = useDrawerContext();

  const handleSearchValueChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
    setSearchValue(event.target.value);
  }, []);

  const navigationSectionsMatchedSearch = useMemo(() => {
    return searchFilterNameOrCategory(searchValue, navigationSections);
  }, [searchValue, navigationSections]);

  const navigationCategoriesMatchedSearch = useMemo(() => {
    return getNestedEntity(navigationSectionsMatchedSearch);
  }, [navigationSectionsMatchedSearch]);

  const navigationCategoriesFiltersMatchedSearch = useMemo(() => {
    return getNestedEntity(navigationCategoriesMatchedSearch);
  }, [navigationCategoriesMatchedSearch]);

  const noSearchResult = useMemo(() => _isEmpty(navigationSectionsMatchedSearch), [navigationSectionsMatchedSearch]);

  const isCategoryMatchedSearch = useMemo(() => {
    return _some(navigationCategoriesMatchedSearch, { value: categoryValue });
  }, [navigationCategoriesMatchedSearch]);

  const shouldCategoryFilterBeVisible = useCallback(
    (filterValue: string) => {
      return _some(navigationCategoriesFiltersMatchedSearch, { value: filterValue });
    },
    [navigationCategoriesFiltersMatchedSearch]
  );

  const getMatchedSearchOptions = useCallback(
    (filterValue: string): SelectOption[] => {
      return _find(navigationCategoriesFiltersMatchedSearch, { value: filterValue })?.options || [];
    },
    [navigationCategoriesFiltersMatchedSearch]
  );

  const assignOptionsToCategoryFilters = useCallback((filtersOptions: FilterValues) => {
    const updatedNavigationSections = getUpdatedNavigationSections({
      categoryValue,
      navigationSections,
      filtersOptions,
    });

    if (updatedNavigationSections) {
      setNavigationSections(updatedNavigationSections);
    }
  }, []);

  return useMemo(
    () => ({
      isSearching,
      isCategoryMatchedSearch,
      noSearchResult,
      searchValue,
      shouldCategoryFilterBeVisible,
      assignOptionsToCategoryFilters,
      getMatchedSearchOptions,
      handleSearchValueChange,
    }),
    [
      isSearching,
      isCategoryMatchedSearch,
      noSearchResult,
      searchValue,
      shouldCategoryFilterBeVisible,
      assignOptionsToCategoryFilters,
      getMatchedSearchOptions,
      handleSearchValueChange,
    ]
  );
};

export default useDrawerSearchFilterNameOrCategory;
